import React from 'react'
import PropTypes from 'prop-types'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import MosSmartDialog from '../MosSmartDialog'
import createDialogTitle from '../../../../generic/utilities/dialogUtil'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import BatchReasonsList from './batchReasons'
import { BATCH_TABLE_CONTAINER_STYLE } from './batchTable'

/**
 * Historia 120 (Jupiter, paquete P3) — `03-dominio.md` §8.3 (regla transversal de los dos
 * `BLOCKED`), D-SP7/D-SP14.
 *
 * 🔒 THE ONLY blocked-batch renderer. N3's `BLOCKED` (pause) and N5's `BLOCKED` (finish) have the
 * SAME shape ON PURPOSE — `{type:'BLOCKED', jobs:[{fabricationOrderId, jobReferenceId,
 * fabricationOrderCode, partReferenceCode, nodeName, reasons}]}` (§22, amendment 13), a 200 that
 * writes nothing — precisely so the client needs ONE renderer and ONE branch per
 * `response.type` in its CALLERS (the pause/finish containers), never a second component here. TWO
 * blocked renderers, or two reason translators, is a DECLARED DEFECT, not a style choice (§7.2
 * #15-bis/#15-ter, D-SP14) — the sibling rule of `batchReasons.jsx` being the one reason renderer.
 *
 * This component does not know or care which gesture (pause/finish) produced the response — it
 * only renders `jobs`. Each blocked job is identified by its OWN fabrication order + job pair
 * (never by array index), and its reasons go through the ONE reason renderer/translator in
 * `batchReasons.jsx` — no local translation, no re-implementation, no split into two lists.
 *
 * It never presents as a network/system error (D-I4/RN44: `BLOCKED` implies ZERO writes and ZERO
 * state changes) — warning tone only, no error role — and it carries no state of its own beyond
 * what `SmartDialog` needs to show/hide: closing it and firing the very same gesture again needs no
 * cleanup, because nothing here was written and every render is driven fully by props.
 */
const BatchBlockedNotice = ({ open = false, jobs = [], onClose = () => {}, title = '' }) => {
  const { t_ } = useSmartTranslation()
  const blockedJobs = Array.isArray(jobs) ? jobs : []

  return (
    <MosSmartDialog
      title={createDialogTitle({ color: 'warning', title: title || t_('Attention'), icon: WarningAmberRoundedIcon })}
      setOpen={open}
      accept={false}
      cancel={false}
      close
      closeCallback={onClose}
    >
      {/* 🔴 S263 (ENMIENDA 13, G3-18/D-SP32) -- NINGÚN IDENTIFICADOR INTERNO EN PANTALLA. Esto decía
          *"Fabrication Order: 129 — Job: 3577"*, y el PO lo vio: son ids, no códigos. Ahora la fila
          muestra los CÓDIGOS que el `BLOCKED` de N3 y N5 publica desde §22 — con las mismas columnas y
          el mismo criterio que la previsualización de cierre (S147/S164), para que los tres diálogos
          del lote se lean igual.
          ⚠️ Los tres códigos viajan SIEMPRE PRESENTES con `null` EXPLÍCITO cuando la fila no se
          resuelve, así que aquí se lee el VALOR y no la presencia. Un `null` como hijo de JSX **no
          pinta nada**: celda vacía, nunca la palabra "null" y NUNCA el id de repuesto — enseñar el
          número sería exactamente lo que esta enmienda retira. El MOTIVO sí se ve siempre: la fila
          sigue diciendo por qué está bloqueada aunque no sepamos nombrarla. */}
      <TableContainer style={BATCH_TABLE_CONTAINER_STYLE}>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t_('Fabrication order')}</TableCell>
              <TableCell>{t_('Reference')}</TableCell>
              <TableCell>{t_('Operation')}</TableCell>
              <TableCell>{t_('Reason')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {blockedJobs.map((job) => (
              <TableRow key={`${job?.fabricationOrderId}-${job?.jobReferenceId}`} hover>
                <TableCell>{job?.fabricationOrderCode}</TableCell>
                <TableCell>{job?.partReferenceCode}</TableCell>
                <TableCell>{job?.nodeName}</TableCell>
                {/* El MISMO renderizador de motivos que las demás superficies (§7.2 #15-bis). */}
                <TableCell><BatchReasonsList reasons={job?.reasons} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </MosSmartDialog>
  )
}

BatchBlockedNotice.propTypes = {
  open: PropTypes.bool,
  jobs: PropTypes.arrayOf(PropTypes.shape({
    fabricationOrderId: PropTypes.number,
    jobReferenceId: PropTypes.number,
    fabricationOrderCode: PropTypes.string,
    partReferenceCode: PropTypes.string,
    nodeName: PropTypes.string,
    reasons: PropTypes.arrayOf(PropTypes.string)
  })),
  onClose: PropTypes.func,
  title: PropTypes.string
}

export default BatchBlockedNotice
